import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Input,
  Select,
  Button,
  Tag,
  Breadcrumb,
  Statistic,
  Divider,
  message,
  Spin,
  Tooltip
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  FireOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  TagOutlined,
  ArrowRightOutlined,
  BellOutlined,
  BellFilled
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import auctionService from '../../services/auctionService';
import notificationService from '../../services/notificationService';
import { isPushSupported, subscribeToPush } from '../../utils/pushNotifications';
import './Categories.css';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const Categories = () => {
  const navigate = useNavigate();
  const { currentUser } = useSelector((state) => state.user);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [subscribedIds, setSubscribedIds] = useState([]);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (currentUser) {
      notificationService.getSubscribedCategoryIds()
        .then(setSubscribedIds)
        .catch((error) => console.error('Error loading subscribed categories:', error));
    }
  }, [currentUser]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await auctionService.getCategoriesWithCounts();
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
      message.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotify = async (e, categoryId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser) {
      message.warning('Please login to get notified about auctions');
      navigate('/login');
      return;
    }

    const willSubscribe = !subscribedIds.includes(categoryId);

    setTogglingId(categoryId);
    try {
      if (willSubscribe && isPushSupported()) {
        try {
          await subscribeToPush();
        } catch (pushError) {
          console.error('Could not enable browser notifications:', pushError);
        }
      }

      const result = await notificationService.toggleCategorySubscription(categoryId);
      setSubscribedIds((prev) =>
        result.is_subscribed ? [...prev, categoryId] : prev.filter((id) => id !== categoryId)
      );
      message.success(result.message);
    } catch (error) {
      console.error('Error toggling category subscription:', error);
      message.error('Failed to update notification subscription');
    } finally {
      setTogglingId(null);
    }
  };

  // Filter categories based on search text
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchText.toLowerCase()) ||
    category.description.toLowerCase().includes(searchText.toLowerCase())
  );

  // Sort categories based on selected sort option
  const sortedCategories = [...filteredCategories].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b.auctions_count - a.auctions_count;
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'ending':
        return new Date(b.ending_at) - new Date(a.ending_at);
      case 'value':
        return b.total_value - a.total_value;
      default:
        return 0;
    }
  });

  return (
    <div className="categories-page">
      {/* Header Section */}
      <div className="categories-header">
        <div className="header-content">
          <Breadcrumb>
            <Breadcrumb.Item><Link to="/">Home</Link></Breadcrumb.Item>
            <Breadcrumb.Item>Categories</Breadcrumb.Item>
          </Breadcrumb>
          <Title level={1}>Browse Categories</Title>
          <Paragraph className="header-description">
            Explore our wide range of categories and discover unique items up for auction.
          </Paragraph>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="search-filter-content">
          <div className="search-box">
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search categories..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
            />
          </div>
          <div className="filter-options">
            <Select
              defaultValue="all"
              style={{ width: 200 }}
              onChange={setSelectedCategory}
              size="large"
            >
              <Option value="all">All Categories</Option>
              {categories.map(category => (
                <Option key={category.id} value={category.name.toLowerCase()}>
                  {category.icon} {category.name}
                </Option>
              ))}
            </Select>
            <Select
              defaultValue="popular"
              style={{ width: 200 }}
              onChange={setSortBy}
              size="large"
            >
              <Option value="popular">Most Popular</Option>
              <Option value="newest">Newest First</Option>
              <Option value="ending">Ending Soon</Option>
              <Option value="value">Highest Value</Option>
            </Select>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="categories-grid">
        {loading ? (
          <Row justify="center" style={{ paddingTop: '150px', minHeight: '100vh' }}>
            <Col>
              <Spin size="large" />
            </Col>
          </Row>
        ) : (
          <Row gutter={[24, 24]}>
            {sortedCategories.map(category => (
              <Col xs={24} sm={12} lg={8} key={category.id}>
                <Link to={`/categories/${category.slug}`}>
                  <Card hoverable className="category-card" style={{ position: 'relative' }}>
                    <Tooltip title={subscribedIds.includes(category.id) ? 'Stop notifying me' : 'Notify me about new auctions'}>
                      <Button
                        shape="circle"
                        size="small"
                        icon={subscribedIds.includes(category.id) ? <BellFilled /> : <BellOutlined />}
                        loading={togglingId === category.id}
                        onClick={(e) => handleToggleNotify(e, category.id)}
                        style={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          zIndex: 2,
                          color: subscribedIds.includes(category.id) ? '#fff' : undefined,
                          background: subscribedIds.includes(category.id) ? '#1890ff' : undefined,
                          borderColor: subscribedIds.includes(category.id) ? '#1890ff' : undefined
                        }}
                      />
                    </Tooltip>
                    <div className="category-header">
                      <div className="category-icon">{category.icon}</div>
                      <Title level={3}>{category.name}</Title>
                    </div>
                    <Paragraph className="category-description">
                      {category.description}
                    </Paragraph>
                    <Divider />
                    <div className="category-stats">
                      <Statistic 
                        title="Auctions" 
                        value={category.auctions_count} 
                        prefix={<TagOutlined />} 
                      />
                      <Statistic 
                        title="Total Lots" 
                        value={category.total_lots || 0} 
                        prefix={<TagOutlined />} 
                      />
                      <Statistic 
                        title="Active Auctions" 
                        value={category.active_auctions_count || 0} 
                        prefix={<FireOutlined />} 
                      />
                    </div>
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* Featured Items Section */}
      <div className="featured-items-section">
        <div className="section-header">
          <Title level={2}>Featured Items</Title>
          <Link to="/auctions" className="view-all">
            View All <ArrowRightOutlined />
          </Link>
        </div>
        <Row gutter={[24, 24]}>
          {featuredItems.map(item => (
            <Col xs={24} sm={12} lg={8} key={item.id}>
              <Card
                hoverable
                cover={<img alt={item.title} src={item.image} />}
                className="featured-item-card"
              >
                <Tag color="blue">{item.category}</Tag>
                <Title level={4}>{item.title}</Title>
                <div className="item-details">
                  <div className="bid-info">
                    <DollarOutlined /> Current Bid: ${item.currentBid.toLocaleString()}
                  </div>
                  <div className="time-info">
                    <ClockCircleOutlined /> {item.timeLeft} left
                  </div>
                  <div className="bids-info">
                    {item.bids} bids
                  </div>
                </div>
                <Button type="primary" block>
                  Place Bid
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

export default Categories; 